(function () {
  'use strict';

  var configuratorRoot = document.querySelector('[data-custom-chopstick-configurator-root]');
  if (!configuratorRoot || configuratorRoot.logoAssets) return;

  var DATABASE_NAME = 'ers-custom-chopstick-configurator';
  var DATABASE_VERSION = 1;
  var STORE_NAME = 'logo-assets';
  var MAX_FILE_SIZE = 10 * 1024 * 1024;
  var MAX_DIMENSION = 12000;
  var MAX_PIXELS = 50000000;
  var MAX_DESIGN_ASSETS = 6;
  var STALE_AFTER = 24 * 60 * 60 * 1000;
  var ALLOWED_TYPES = {
    'image/jpeg': true,
    'image/png': true,
    'image/webp': true
  };
  var memoryRecords = {};
  var databasePromise = null;

  function LogoAssetError(code, message) {
    this.name = 'LogoAssetError';
    this.code = code;
    this.message = message;
  }
  LogoAssetError.prototype = Object.create(Error.prototype);
  LogoAssetError.prototype.constructor = LogoAssetError;

  function openDatabase() {
    if (!window.indexedDB) return Promise.reject(new Error('IndexedDB is unavailable.'));
    if (databasePromise) return databasePromise;

    databasePromise = new Promise(function (resolve, reject) {
      var request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = function () {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
      request.onsuccess = function () {
        request.result.onversionchange = function () { request.result.close(); };
        resolve(request.result);
      };
      request.onerror = function () {
        databasePromise = null;
        reject(request.error || new Error('Logo storage could not be opened.'));
      };
      request.onblocked = function () {
        databasePromise = null;
        reject(new Error('Logo storage is blocked by another page.'));
      };
    });

    return databasePromise;
  }

  function putPersistentRecord(record) {
    return openDatabase().then(function (database) {
      return new Promise(function (resolve, reject) {
        var transaction = database.transaction(STORE_NAME, 'readwrite');
        transaction.objectStore(STORE_NAME).put(record);
        transaction.oncomplete = function () { resolve(true); };
        transaction.onerror = function () { reject(transaction.error || new Error('Logo could not be stored.')); };
        transaction.onabort = function () { reject(transaction.error || new Error('Logo storage was interrupted.')); };
      });
    });
  }

  function getPersistentRecord(assetId) {
    return openDatabase().then(function (database) {
      return new Promise(function (resolve, reject) {
        var transaction = database.transaction(STORE_NAME, 'readonly');
        var request = transaction.objectStore(STORE_NAME).get(assetId);
        request.onsuccess = function () { resolve(request.result || null); };
        request.onerror = function () { reject(request.error || new Error('Logo could not be restored.')); };
      });
    });
  }

  function removePersistentRecord(assetId) {
    return openDatabase().then(function (database) {
      return new Promise(function (resolve, reject) {
        var transaction = database.transaction(STORE_NAME, 'readwrite');
        transaction.objectStore(STORE_NAME).delete(assetId);
        transaction.oncomplete = function () { resolve(true); };
        transaction.onerror = function () { reject(transaction.error || new Error('Logo could not be removed.')); };
        transaction.onabort = function () { reject(transaction.error || new Error('Logo removal was interrupted.')); };
      });
    });
  }

  function decodeImage(file) {
    return new Promise(function (resolve, reject) {
      var objectUrl = URL.createObjectURL(file);
      var image = new Image();
      image.decoding = 'async';
      image.onload = function () {
        var width = image.naturalWidth;
        var height = image.naturalHeight;
        URL.revokeObjectURL(objectUrl);
        if (!width || !height) {
          reject(new LogoAssetError('corrupt', 'That file does not contain a usable image.'));
          return;
        }
        resolve({ width: width, height: height });
      };
      image.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        reject(new LogoAssetError('corrupt', 'That image could not be read. Try another file.'));
      };
      image.src = objectUrl;
    });
  }

  function inspect(file) {
    if (!file || typeof file.size !== 'number') {
      return Promise.reject(new LogoAssetError('missing', 'Choose a logo file to continue.'));
    }

    var type = String(file.type || '').toLowerCase();
    if (!ALLOWED_TYPES[type]) {
      return Promise.reject(new LogoAssetError('type', 'Use a PNG, JPG, or WebP logo.'));
    }
    if (!file.size) {
      return Promise.reject(new LogoAssetError('empty', 'That logo file is empty.'));
    }
    if (file.size > MAX_FILE_SIZE) {
      return Promise.reject(new LogoAssetError('size', 'Choose a logo smaller than 10 MB.'));
    }

    return decodeImage(file).then(function (dimensions) {
      if (
        dimensions.width > MAX_DIMENSION ||
        dimensions.height > MAX_DIMENSION ||
        dimensions.width * dimensions.height > MAX_PIXELS
      ) {
        throw new LogoAssetError('dimensions', 'That image is too large to process safely.');
      }
      return {
        name: String(file.name || 'Restaurant logo').slice(0, 180),
        type: type,
        size: file.size,
        width: dimensions.width,
        height: dimensions.height,
        lastModified: typeof file.lastModified === 'number' ? file.lastModified : null
      };
    });
  }

  function createAssetId(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var randomPart = Math.random().toString(36).slice(2);
    return (prefix || 'image') + '-' + Date.now().toString(36) + '-' + randomPart;
  }

  function stateMetadata(record) {
    return {
      assetId: record.id,
      name: record.name,
      type: record.type,
      size: record.size,
      width: record.width,
      height: record.height,
      lastModified: record.lastModified,
      updatedAt: record.updatedAt
    };
  }

  function saveRecord(file, purpose) {
    return inspect(file).then(function (metadata) {
      var record = {
        id: createAssetId(purpose === 'design-image' ? 'design-image' : 'logo'),
        purpose: purpose,
        blob: file,
        name: metadata.name,
        type: metadata.type,
        size: metadata.size,
        width: metadata.width,
        height: metadata.height,
        lastModified: metadata.lastModified,
        updatedAt: Date.now()
      };
      memoryRecords[record.id] = record;

      return putPersistentRecord(record).then(function () {
        configuratorRoot.setAttribute('data-configurator-logo-storage', 'indexeddb');
        return { metadata: stateMetadata(record), record: record, persisted: true };
      }).catch(function () {
        configuratorRoot.setAttribute('data-configurator-logo-storage', 'memory');
        return { metadata: stateMetadata(record), record: record, persisted: false };
      });
    });
  }

  function save(file) {
    return saveRecord(file, 'semantic-logo');
  }

  function saveDesignImage(file) {
    return saveRecord(file, 'design-image');
  }

  function get(assetId) {
    if (!assetId || typeof assetId !== 'string') return Promise.resolve(null);
    if (memoryRecords[assetId]) return Promise.resolve(memoryRecords[assetId]);
    return getPersistentRecord(assetId).then(function (record) {
      if (record) memoryRecords[assetId] = record;
      return record;
    }).catch(function () {
      return null;
    });
  }

  function remove(assetId) {
    if (!assetId) return Promise.resolve(false);
    delete memoryRecords[assetId];
    return removePersistentRecord(assetId).catch(function () { return false; });
  }

  function prune(keepAssetIds) {
    var keep = {};
    (keepAssetIds || []).forEach(function (assetId) { keep[assetId] = true; });
    var staleBefore = Date.now() - STALE_AFTER;

    Object.keys(memoryRecords).forEach(function (assetId) {
      if (!keep[assetId] && memoryRecords[assetId].updatedAt < staleBefore) delete memoryRecords[assetId];
    });

    return openDatabase().then(function (database) {
      return new Promise(function (resolve, reject) {
        var transaction = database.transaction(STORE_NAME, 'readwrite');
        var request = transaction.objectStore(STORE_NAME).openCursor();
        request.onsuccess = function () {
          var cursor = request.result;
          if (!cursor) return;
          var record = cursor.value;
          if (!keep[record.id] && record.updatedAt < staleBefore) cursor.delete();
          cursor.continue();
        };
        transaction.oncomplete = function () { resolve(true); };
        transaction.onerror = function () { reject(transaction.error || new Error('Logo cleanup failed.')); };
      });
    }).catch(function () { return false; });
  }

  configuratorRoot.logoAssets = {
    save: save,
    saveDesignImage: saveDesignImage,
    get: get,
    remove: remove,
    prune: prune,
    constraints: {
      allowedTypes: Object.keys(ALLOWED_TYPES),
      maximumBytes: MAX_FILE_SIZE,
      maximumDesignAssets: MAX_DESIGN_ASSETS
    }
  };
}());
